import { Skeleton, Stack } from '@mantine/core';
import { useState } from 'react';
import useTranslation from 'next-translate/useTranslation';

import { Routes } from '~/domains/common/constants/routes';
import { useContest } from '~/domains/contest/hooks/useContest';
import dt from '~/domains/contest/domains/manage/domains/common/testing.const';
import { Button, ButtonLink } from '~/domains/design-system/Button';
import InviteModal from '~/domains/contest/domains/invite/components/InviteModal';
import ContestCard from '~/domains/contest/domains/common/components/ContestCard/ContestCard';
import useContestInvite from '~/domains/contest/domains/invite/hooks/useContestInvite';
import { useInvitesManagerEnabled } from '~/domains/invites-manager/hooks/useInvitesManagerEnabled';
import isWebView from '~/utils/isWebView';

function InviteButton({ contestId, onClick }: { contestId: string; onClick?: () => void }) {
  const { t } = useTranslation('contest');
  const { isInvitesManagerEnabled } = useInvitesManagerEnabled();

  if (isInvitesManagerEnabled || isWebView()) {
    return (
      <ButtonLink
        href={Routes.invitesManagerNew(contestId)}
        data-test-id={dt.action.invite}
        variant="primary-fill"
      >
        {t('createContest.success.inviteMembers')}
      </ButtonLink>
    );
  }

  return (
    <Button data-test-id={dt.action.invite} variant="primary-fill" onClick={onClick}>
      {t('createContest.success.inviteMembers')}
    </Button>
  );
}

type SectionSubmitScreenProps = {
  contestId: string;
};

export function SectionSubmitScreen({ contestId }: SectionSubmitScreenProps) {
  const { isInviteCTAShown } = useContestInvite();
  const { t } = useTranslation('contest');
  const [inviteModalContestId, setInviteModalContestId] = useState<string>(null);

  const fullContest = useContest(contestId, true, 0);

  return (
    <Stack
      data-test-id={dt.screen.submitted}
      mih={300}
      w={{ base: '100%', sm: 390 }}
      justify="center"
      /* eslint-disable-line local-rules/use-theme-tokens */ spacing={32}
      ml="auto"
      mr="auto"
      /* eslint-disable-line local-rules/use-theme-tokens */ mt={16}
    >
      {fullContest.isSuccess ? (
        <ContestCard isStatic isCardMenuVisible={false} contest={fullContest.data} />
      ) : (
        <Skeleton width="100%" mih={160} />
      )}
      <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={8}>
        {isInviteCTAShown ? (
          <InviteButton contestId={contestId} onClick={() => setInviteModalContestId(contestId)} />
        ) : null}
        <ButtonLink
          data-test-id={dt.action.view}
          variant="secondary-stroke"
          href={Routes.contestDetail(contestId)}
        >
          {t('createContest.success.viewInvite')}
        </ButtonLink>
      </Stack>
      <InviteModal
        opened={!!inviteModalContestId}
        onClose={() => setInviteModalContestId(null)}
        contestId={inviteModalContestId}
        contestName={fullContest.data?.name}
      />
    </Stack>
  );
}
